import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { InterestList, SuggestedPoint } from '../../../interfaces/edit-route.response';
import { normalizeString } from 'src/app/utils/helper-string';
import { EditedCustomRoute, EditedPoint } from '../../../interfaces/edit-route.interface';
import { EditRouteService } from '../../../services/edit-route.service';

@Injectable({
  providedIn: 'root'
})
export class SuggestedPointsService {
  public interestList: WritableSignal<InterestList[]> = signal<InterestList[]>([]);
  public suggestedPoints: WritableSignal<SuggestedPoint[]> = signal<SuggestedPoint[]>([]);
  public filteredSuggestedPoints: WritableSignal<SuggestedPoint[]> = signal<SuggestedPoint[]>([]);

  private _editRouteService = inject(EditRouteService);
  
  private _customRouteEditedDataToRender = this._editRouteService.customRouteEditedDataToRender;
  
  resetSuggestedPoints(): void {
    this.suggestedPoints.set([]);
  }
  
  /**
   * Filters suggested points based on the end date of the previous point to be replaced.
   * This ensures that suggested points do not overlap with the end date of the previous point.
   */
  filterSuggestedPointsByEndDatePreviousPoint(
    suggestedPoints: SuggestedPoint[],
    currentPointToBeReplaced: SuggestedPoint | EditedPoint,
    dayToShowIndex: number
  ): SuggestedPoint[] 
  {
    if (!currentPointToBeReplaced || !this._customRouteEditedDataToRender()) return suggestedPoints;

    console.log('Current point to be replaced:', currentPointToBeReplaced);
    
    const indexCurrentPointToBeReplaced = this._customRouteEditedDataToRender()?.dailyRoutes[dayToShowIndex].points.findIndex(point => point.id === currentPointToBeReplaced.id && point.type === currentPointToBeReplaced.type);
    const previousPointToBeReplaced = indexCurrentPointToBeReplaced > 0
      ? this._customRouteEditedDataToRender()?.dailyRoutes[dayToShowIndex].points[indexCurrentPointToBeReplaced - 1]
      : null;
    
    if (!previousPointToBeReplaced) return suggestedPoints;
      
    console.log('Previous point to be replaced:', previousPointToBeReplaced);
      
    // get endDate of the previous point to be replaced or the current point to be replaced
    const endDatePreviousPointToBeReplaced = previousPointToBeReplaced.endDate

    if (!endDatePreviousPointToBeReplaced) return suggestedPoints;
    
    //TODO: Check right times or ignore Start Time
    // remove the suggested points that are before the end date of the previous point to be replaced
    return suggestedPoints;
    // return suggestedPoints.filter(point => {
    //   return dayjs(point.startDate).isAfter(dayjs(endDatePreviousPointToBeReplaced));
    // });
  }
  
  /**
   * Filters suggested points based on suggestedPoints already selected in the edit route.
   * This way we avoid showing suggested points that they have already been selected in the edit-route
   */
  filterSuggestedPointsByAlreadySelectedPoints(suggestedPoints: SuggestedPoint[]): SuggestedPoint[] {
    const suggestedPointsAlreadySelected = this.getSuggestedPointsAlreadySelected(this._customRouteEditedDataToRender());
    if (suggestedPointsAlreadySelected.length === 0) {
      return suggestedPoints;
    }
    
    return suggestedPoints.filter(point => {
      return !suggestedPointsAlreadySelected.some(editedPoint =>
        editedPoint.id === point.id && editedPoint.type === point.type
      );
    });
  } 
  
  /**
   * Clean suggested points according to the customRouteEditedDataToRender.
   * This way we avoid showing suggested points that they have already been selected in the edit-route.
   */
  getSuggestedPointsAlreadySelected(customRouteEditedDataToRender: EditedCustomRoute): SuggestedPoint[] {
    if (!customRouteEditedDataToRender) return [];

    const currentRenderPoints = customRouteEditedDataToRender.dailyRoutes.flatMap(day => day.points);

    const alreadySelected = this.suggestedPoints().filter(point => 
      currentRenderPoints.some(editedPoint => 
        editedPoint.id === point.id && editedPoint.type === point.type
      )
    );
    
    return alreadySelected;
  }

  /**
   * Logic to filter suggested points based on interest Ids.
   */
  applyInterestsIdsFilter(ids: number[], suggestedPoints: SuggestedPoint[]): void {
    const filteredPoints = this.filterByIds(ids, suggestedPoints);

    if (filteredPoints?.length > 0) 
      this.filteredSuggestedPoints.set(filteredPoints);
    else 
      this.filteredSuggestedPoints.set([]);
  }

  filterByIds(ids: number[], points: SuggestedPoint[]): SuggestedPoint[] {
    if (ids?.length === 0)
      return points

    const filtered = points.filter(point => point.categoryList?.some(category => ids.includes(category.id)));
    return filtered;
  }
  
  /**
   * Logic to filter suggested points based on the search term.
   */
  applySearchTermFilter(searchTerm: string, suggestedPoints: SuggestedPoint[]): SuggestedPoint[] {
    const filteredPoints = this.filterByTerm(searchTerm.trim(), suggestedPoints);

    if (filteredPoints?.length > 0) {
      this.filteredSuggestedPoints.set(filteredPoints);
    } else {
      this.filteredSuggestedPoints.set([]);
    }

    return filteredPoints;
  }
  
  filterByTerm(term: string, points: SuggestedPoint[]): SuggestedPoint[] {
    // If the term is empty or has less than 3 characters, do not filter
    if (!term || term.trim().length < 3) {
      return points;
    }
    
    const normalizeTerm = normalizeString(term.trim(), false);

    // Step 1: Exact Matches
    const exactMatches = points.filter(item => {
      const normalizePointName = normalizeString(item.title, true);
      return this.hasExactWordMatch(normalizePointName, normalizeTerm);
    });

    // if there are exact matches, return only those
    if (exactMatches.length > 0) {
      return exactMatches;
    }

    // Step 2: If no exact matches are found, look for similarities (3+ characters)
    return points.filter(item => {
      const normalizePointName = normalizeString(item.title, true);
      const result = this.matchesFromWordStart(normalizePointName, normalizeTerm);
      
      return result;
    });
  }

  hasExactWordMatch(itemName: string, searchTerm: string): boolean {
    const words = itemName.split(' ');
    const searchLower = searchTerm.toLowerCase();
    
    return words.some(word => word.toLowerCase() === searchLower);
  }

  matchesFromWordStart(itemName: string, searchTerm: string): boolean {
    // Dividir el título en palabras
    const words = itemName.split(' ');
    
    // Para cada palabra, verificar si coincide desde el inicio con al menos 3 caracteres
    for (const word of words) {
      const wordLower = word.toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      // Verificar cuántos caracteres coinciden desde el inicio
      const minLength = Math.min(wordLower.length, searchLower.length);
      
      if (minLength >= 3) {
        // Verificar si los caracteres desde el inicio coinciden
        let matchCount = 0;
        for (let i = 0; i < minLength; i++) {
          if (wordLower[i] === searchLower[i]) {
            matchCount++;
          } else {
            break; // Si no coincide un carácter, parar
          }
        }
        
        // Si coinciden 3 o más caracteres desde el inicio, es válido
        if (matchCount >= 3) {
          console.log(`✅ Word "${word}" matches "${searchTerm}" with ${matchCount} chars from start`);
          return true;
        }
      }
    }
    
    return false;
  }
  
  hasConsecutiveChars(itemName: string, searchTerm: string): boolean {
    const normalizedItem = normalizeString(itemName, false);
    const normalizedSearch = normalizeString(searchTerm, false);

    // Si el término de búsqueda tiene menos de 3 caracteres, no aplicar esta lógica
    if (normalizedSearch.length < 3) return false;

    // Dividir el nombre del item en palabras
    const words = normalizedItem.split(' ');
    
    // Para cada palabra del item.title
    for (const word of words) {
      // Verificar si la palabra comienza con el término de búsqueda completo
      if (word.length >= normalizedSearch.length && word.startsWith(normalizedSearch)) {
        return true;
      }
      // Como respaldo, verificar si al menos los primeros 3 caracteres coinciden
      if (word.length >= 3 && word.substring(0, 3) === normalizedSearch.substring(0, 3)) {
        return true;
      }
    }
    return false;
  }

  isSimilar(itemName: string, searchTerm: string): boolean {
    const itemNameWords = normalizeString(itemName, true).split(' ');
    const searchTermWords = normalizeString(searchTerm, true).split(' ');

    for (const word of itemNameWords) {
      for (const searchWord of searchTermWords) {
        if (this.levenshteinDistance(word, searchWord) <= 2) {
          return true;
        }
      }
    }
    return false;
  }


  levenshteinDistance(a: string, b: string): number {
    const matrix = [];

    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            Math.min(
              matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1 // deletion
            )
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }
}
